import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Play, Pause, Share2, ChevronLeft, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Watch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    api.get(`/episodes/${id}`).then(({ data }) => setEpisode(data)).catch(() => {
      toast.error("Episode not found");
      navigate("/dashboard");
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  // Auto-advance through media as audio plays
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !episode) return;
    const onTime = () => {
      const dur = a.duration || 1;
      const p = a.currentTime / dur;
      setProgress(p);
      const total = episode.media_ids?.length || 1;
      setCurrentChapter(Math.min(total - 1, Math.floor(p * total)));
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [episode]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const handleShare = async () => {
    if (!episode) return;
    const shareUrl = `${window.location.origin}/share/${episode.share_slug}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Trailer link copied — drop it in the group chat");
    } catch {
      toast.message(shareUrl);
    }
  };

  const handleDelete = async () => {
    if (!episode) return;
    if (!window.confirm("Delete this episode?")) return;
    await api.delete(`/episodes/${episode.id}`);
    navigate("/dashboard");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#FF2A2A]" />
    </div>
  );
  if (!episode) return null;

  const script = episode.script || {};
  const chapters = script.chapters || [];
  const mediaIds = episode.media_ids || [];
  const currentMediaId = mediaIds[currentChapter] || mediaIds[0];
  const currentNarration = chapters[currentChapter % Math.max(chapters.length, 1)]?.narration || script.intro || "";
  const currentChapterTitle = chapters[currentChapter % Math.max(chapters.length, 1)]?.title || "";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="watch-page">
      <Navbar />

      <div className="pt-20">
        {/* Top bar */}
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-6 flex items-center justify-between">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate("/dashboard")}
            className="font-mono text-xs uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> All episodes
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="share-button"
              onClick={handleShare}
              className="font-mono text-xs uppercase tracking-widest border border-white/20 hover:border-white/60 px-4 py-2 inline-flex items-center gap-2 transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
            <button
              data-testid="delete-button"
              onClick={handleDelete}
              className="font-mono text-xs uppercase tracking-widest border border-white/10 hover:border-[#FF2A2A] hover:text-[#FF2A2A] px-3 py-2 inline-flex items-center gap-2 transition-colors text-white/40"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {episode.status === "generating" && (
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#FF2A2A] mx-auto" />
            <h2 className="font-display font-black text-3xl tracking-tighter mt-6">Rolling cameras...</h2>
            <p className="font-emotional text-white/60 mt-2">Claude is writing your narration. OpenAI is voicing it.</p>
          </div>
        )}

        {episode.status === "failed" && (
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <h2 className="font-display font-black text-3xl tracking-tighter">Something broke on set.</h2>
            <p className="font-mono text-xs uppercase tracking-widest text-white/50 mt-4">{episode.error || "Try again"}</p>
          </div>
        )}

        {episode.status === "ready" && (
          <>
            {/* CINEMA FRAME */}
            <div className="max-w-6xl mx-auto px-6 sm:px-12">
              <div data-testid="cinema-frame" className="relative aspect-[16/10] bg-black overflow-hidden border border-white/10">
                {currentMediaId && (
                  <img
                    key={currentMediaId}
                    src={fileUrl(currentMediaId)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover cinema-fade-in"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-black/40" />

                {/* Chapter label */}
                <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.4em] text-white/80 bg-black/60 backdrop-blur px-3 py-1.5">
                  CH {String(currentChapter + 1).padStart(2, "0")} · {currentChapterTitle}
                </div>

                {/* Narration overlay */}
                <div className="absolute bottom-20 left-0 right-0 text-center px-8">
                  <p className="font-emotional text-2xl sm:text-4xl text-white leading-snug max-w-4xl mx-auto drop-shadow-[0_2px_20px_rgba(0,0,0,0.9)]">
                    {`"${currentNarration}"`}
                  </p>
                </div>

                {/* Progress bar */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                  <div
                    data-testid="progress-bar"
                    className="h-full bg-[#FF2A2A] transition-all duration-200"
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>

                {/* Play button */}
                <button
                  data-testid="play-button"
                  onClick={togglePlay}
                  className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-[#FF2A2A]/90 hover:bg-[#FF2A2A] backdrop-blur flex items-center justify-center transition-all hover:scale-110"
                  style={{ display: playing ? "none" : "flex" }}
                >
                  <Play className="w-9 h-9 fill-white text-white ml-1" />
                </button>
                {playing && (
                  <button
                    data-testid="pause-button"
                    onClick={togglePlay}
                    className="absolute bottom-6 right-6 w-12 h-12 rounded-full bg-black/60 hover:bg-black backdrop-blur flex items-center justify-center transition-all"
                  >
                    <Pause className="w-5 h-5 fill-white text-white" />
                  </button>
                )}
              </div>

              {episode.audio_file_id && (
                <audio
                  ref={audioRef}
                  src={fileUrl(episode.audio_file_id)}
                  preload="auto"
                />
              )}
            </div>

            {/* META */}
            <div className="max-w-6xl mx-auto px-6 sm:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF2A2A] mb-3">
                  {episode.vibe} · LIFEREPLAY ORIGINAL
                </div>
                <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-none">
                  {episode.title}
                </h1>
                {script.tagline && (
                  <p className="font-emotional text-2xl text-white/60 mt-6">{`"${script.tagline}"`}</p>
                )}
                {script.outro && (
                  <p className="text-white/50 mt-8 max-w-2xl leading-relaxed">{script.outro}</p>
                )}
              </div>

              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
                  Chapter list
                </div>
                <div className="space-y-3">
                  {chapters.map((c, i) => (
                    <button
                      key={`${c.title}-${i}`}
                      data-testid={`chapter-${i}`}
                      onClick={() => setCurrentChapter(i)}
                      className={`w-full text-left p-3 border-l-2 transition-all ${
                        i === currentChapter
                          ? "border-[#FF2A2A] bg-white/[0.03]"
                          : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        CH {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="font-display font-bold text-sm tracking-tight mt-1">
                        {c.title}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 mt-12 pt-6 border-t border-white/10">
                  {script.credits || "A LifeReplay Original"}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
